import { useState, useEffect } from 'react'
import { useCompany } from '@/hooks/useCompany'
import { auditLogService } from '@/services/audit_log.service'
import type { AuditLog } from '@/types/audit_log.types'

interface TimelineAuditTrailProps {
  tableName: string
  entityId: string
}

export function TimelineAuditTrail({ tableName, entityId }: TimelineAuditTrailProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Collapse automatically if entityId changes (navigation between items)
  useEffect(() => {
    setIsExpanded(false)
  }, [entityId])

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md, 8px)',
      overflow: 'hidden',
      marginBottom: '1rem',
      background: 'var(--surface)'
    }}>
      <button
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '1rem',
          border: 'none',
          background: 'var(--surface-2)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: '0.95rem',
          fontWeight: 600,
          color: 'var(--text-1)',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--surface-3)' }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--surface-2)' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-3)' }}>
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          Histórico de Alterações (Trilha de Auditoria)
        </span>
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2.5" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ 
            transform: isExpanded ? 'rotate(180deg)' : 'none', 
            transition: 'transform 0.2s' 
          }}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {isExpanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '1.25rem' }}>
          <TimelineAuditTrailContent tableName={tableName} entityId={entityId} />
        </div>
      )}
    </div>
  )
}

function TimelineAuditTrailContent({ tableName, entityId }: TimelineAuditTrailProps) {
  const { activeCompany } = useCompany()
  const companyId = activeCompany?.id ?? ''

  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!companyId || !tableName || !entityId) return
    void (async () => {
      try {
        setLoading(true)
        const data = await auditLogService.listLogs(companyId, tableName, entityId)
        setLogs(data)
      } catch (err: any) {
        setError('Erro ao carregar trilha de auditoria.')
      } finally {
        setLoading(false)
      }
    })()
  }, [companyId, tableName, entityId])

  // Field names mapping in Portuguese
  const fieldLabels: Record<string, string> = {
    type: 'Tipo',
    description: 'Descrição',
    amount: 'Valor',
    amount_paid: 'Valor Pago',
    date: 'Data Projetada',
    payment_date: 'Data Pagamento',
    billing_date: 'Data Faturamento',
    bank_account_id: 'Conta Bancária',
    client_id: 'Cliente',
    employee_id: 'Colaborador',
    service_id: 'Serviço',
    payment_method_id: 'Forma de Pagamento',
    reference: 'Referência / Nº doc',
    notes: 'Observações',
    category_id: 'Categoria Analítica',
    name: 'Nome / Razão Social',
    cnpj: 'CNPJ',
    active: 'Status',
    account_type: 'Tipo de Conta',
  }

  // Helper function to format and translate values nicely
  const formatValue = (field: string, val: any, delta?: any, type?: 'old' | 'new') => {
    if (delta) {
      if (type === 'old' && delta.oldLabel !== undefined) return delta.oldLabel || '—'
      if (type === 'new' && delta.newLabel !== undefined) return delta.newLabel || '—'
    }

    if (val === null || val === undefined || val === '') return '—'

    // Currency fields
    if (field === 'amount' || field === 'amount_paid') {
      const num = typeof val === 'string' ? parseFloat(val) : val
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num)
    }

    // Dates
    if (field === 'date' || field === 'payment_date' || field === 'billing_date') {
      if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = val.split('-')
        return `${day}/${month}/${year}`
      }
    }

    // Type field
    if (field === 'type') {
      return val === 'entrada' ? 'Entrada' : val === 'saida' ? 'Saída' : val
    }

    // Booleans
    if (typeof val === 'boolean') {
      if (field === 'active') {
        return val ? 'Ativo' : 'Inativo'
      }
      return val ? 'Sim' : 'Não'
    }

    // Account Type
    if (field === 'account_type') {
      return val === 'business' ? 'Pessoa Jurídica' : val === 'personal' ? 'Pessoa Física' : val
    }

    return String(val)
  }

  // Format log creation time
  const formatDateTime = (isoString: string) => {
    const dateObj = new Date(isoString)
    const formattedDate = dateObj.toLocaleDateString('pt-BR')
    const formattedTime = dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    return `${formattedDate} às ${formattedTime}`
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', color: 'var(--text-3)', fontSize: '13.5px', fontStyle: 'italic' }}>
        <div className="ui-loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        <span>Carregando histórico...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="ui-alert ui-alert--error" style={{ fontSize: '13.5px' }}>{error}</div>
    )
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin { to { transform: rotate(360deg); } }
        
        .audit-timeline {
          position: relative;
          padding-left: 20px;
          margin: 8px 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .audit-timeline::before {
          content: '';
          position: absolute;
          left: 5px;
          top: 6px;
          bottom: 6px;
          width: 2px;
          background: var(--border);
        }
        
        .audit-item {
          position: relative;
          padding-bottom: 2px;
          transition: all var(--transition);
        }
        
        .audit-dot {
          position: absolute;
          left: -20px;
          top: 5px;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid var(--surface);
          box-shadow: var(--shadow-xs);
        }
        
        .audit-dot--insert { background: var(--success); }
        .audit-dot--update { background: var(--info); }
        .audit-dot--delete { background: var(--danger); }
        
        .audit-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 6px;
          font-size: 13.5px;
          font-weight: 600;
          color: var(--text-1);
        }
        
        .audit-time {
          font-size: 11.5px;
          font-weight: 500;
          color: var(--text-3);
        }
        
        .audit-details {
          margin-top: 4px;
          padding: 8px 12px;
          background: var(--surface-2);
          border-radius: var(--radius-sm);
          border: 1.5px solid var(--border);
          display: flex;
          flex-direction: column;
          gap: 4px;
          font-size: 12.5px;
          color: var(--text-2);
        }
        
        .audit-detail-line {
          display: inline-flex;
          flex-wrap: wrap;
          gap: 4px;
          align-items: center;
        }
        
        .audit-field-name {
          font-weight: 700;
          color: var(--text-1);
        }
        
        .audit-old-val {
          color: var(--text-3);
          text-decoration: line-through;
          background: color-mix(in srgb, var(--danger) 8%, transparent);
          padding: 1px 4px;
          border-radius: 3px;
        }
        
        .audit-arrow {
          color: var(--text-3);
          font-size: 10px;
          padding: 0 2px;
        }
        
        .audit-new-val {
          color: var(--success);
          font-weight: 600;
          background: color-mix(in srgb, var(--success) 8%, transparent);
          padding: 1px 4px;
          border-radius: 3px;
        }
      ` }} />

      {logs.length === 0 ? (
        <div style={{ color: 'var(--text-3)', fontSize: '13px', fontStyle: 'italic', padding: '8px 4px' }}>
          Nenhum histórico de alterações registrado para este lançamento.
        </div>
      ) : (
        <div className="audit-timeline">
          {logs.map((log) => {
            const actorName = log.actor?.name || 'Usuário do Sistema'
            const actorEmail = log.actor?.email ? ` (${log.actor.email})` : ''
            const actionClass = `audit-dot--${log.action}`
            
            return (
              <div key={log.id} className="audit-item">
                <div className={`audit-dot ${actionClass}`} />
                
                <div className="audit-header">
                  <span>
                    {log.action === 'insert' && (
                      <span>{tableName === 'companies' ? 'Empresa cadastrada' : 'Lançamento registrado'} por <strong style={{ color: 'var(--success)' }}>{actorName}</strong>{actorEmail}</span>
                    )}
                    {log.action === 'delete' && (
                      <span>{tableName === 'companies' ? 'Empresa inativada' : 'Lançamento inativado'} por <strong style={{ color: 'var(--danger)' }}>{actorName}</strong>{actorEmail}</span>
                    )}
                    {log.action === 'update' && (
                      <span>Alterado por <strong>{actorName}</strong>{actorEmail}</span>
                    )}
                  </span>
                  <span className="audit-time">{formatDateTime(log.created_at)}</span>
                </div>

                {log.action === 'update' && Object.keys(log.changes).length > 0 && (
                  <div className="audit-details">
                    {Object.entries(log.changes).map(([field, delta]) => {
                      const fieldLabel = fieldLabels[field] || field
                      return (
                        <div key={field} className="audit-detail-line">
                          <span className="audit-field-name">{fieldLabel}:</span>
                          <span className="audit-old-val">{formatValue(field, delta.old, delta, 'old')}</span>
                          <span className="audit-arrow">➔</span>
                          <span className="audit-new-val">{formatValue(field, delta.new, delta, 'new')}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
